import './NumberInput.less';
import React from 'react';
import Icon from '../icon/Icon';
import {getLocale} from "../../i18n";
import {regexEscapeString} from "../../utils/regex";


export default class NumberInput extends React.Component {
    constructor(props) {
        super(props);

        // Instead of verifying whenever props changed,
        // we verify only one time when initializing for better performance
        NumberInput.verifyProps(this.props);

        this.state = NumberInput.modifyState({
            focused: false,
        }, this.props.defaultValue);
    }

    static modifyState(state, value) {
        state.value = NumberInput.normalizeValue(value);

        return state;
    }

    static getDerivedStateFromProps(props, state) {
        if (props.value !== undefined) {
            return NumberInput.modifyState(state, props.value);
        }

        if (props.imperativeValue !== state.prevImperativeValue) {
            state.prevImperativeValue = props.imperativeValue;

            return NumberInput.modifyState(state, props.imperativeValue);
        }

        return null;
    }

    static normalizeValue(value) {
        return value === '' || value === null || isNaN(value) ? null : Number(value);
    }

    static verifyProps(props) {
        const {
            negativeSign,
            negativeSignAliases,
            negativeSignTriggers,
            decimalPoint,
            decimalPointAliases,
            decimalPointTriggers,
            groupSeparator,
            groupSeparatorAliases,
        } = props;

        const triggers = [...negativeSignTriggers, ...decimalPointTriggers];

        for (let i = 0; i < triggers.length; i++) {
            const trigger = triggers[i];
            if (trigger.length !== 1) {
                throw new Error(`The trigger must be a character: ${trigger} contains ${trigger.length} characters`);
            }
            if (trigger.match(/\d/) !== null) {
                throw new Error(`The trigger cannot be a digit: ${trigger} is invalid`);
            }
        }

        const negativeSignTokens = [negativeSign, ...negativeSignAliases];
        const decimalPointTokens = [decimalPoint, ...decimalPointAliases];
        const groupSeparatorTokens = [groupSeparator, ...groupSeparatorAliases];

        const tokens = [...negativeSignTokens, ...decimalPointTokens, ...groupSeparatorTokens];

        for (let i = 0; i < tokens.length; i++) {
            const token = tokens[i];
            if (token.length === 0) {
                throw new Error('The token cannot be empty');
            }
            if (token.match(/\d/) !== null) {
                throw new Error(`The token cannot contain digits: ${token} is invalid`);
            }
        }

        for (let i = 0; i < groupSeparatorTokens.length; i++) {
            const gst = groupSeparatorTokens[i];
            for (let j = 0; j < decimalPointTokens.length; j++) {
                const dpt = decimalPointTokens[j];
                if (gst.includes(dpt)) {
                    throw new Error(`The group separator cannot include a decimal point: ${gst} contains ${dpt}`);
                }
                // if (dpt.includes(gst)) {
                //     throw new Error(`The decimal point cannot include a group separator: ${dpt} contains ${gst}`);
                // }
                // for (let k = 0; k < negativeSignTokens.length; k++) {
                //     const nst = negativeSignTokens[k];
                //     if (gst.includes(nst)) {
                //         throw new Error(`The group separator cannot include a negative sign: ${gst} contains ${nst}`);
                //     }
                //     if (nst.includes(gst)) {
                //         throw new Error(`The negative sign cannot include a group separator: ${nst} contains ${gst}`);
                //     }
                //     if (dpt.includes(nst)) {
                //         throw new Error(`The decimal point cannot include a negative sign: ${dpt} contains ${nst}`);
                //     }
                //     if (nst.includes(dpt)) {
                //         throw new Error(`The negative sign cannot include a decimal point: ${nst} contains ${dpt}`);
                //     }
                // }
            }
        }
    }

    componentDidUpdate() {
        const expectedString = this.inputValueFromNumber(this.state.value);
        const currentString = this.inputValueFromNumber(this.stringToNumber(this.inputEl.value, true, true));

        if (expectedString !== currentString) {
            this.inputEl.value = expectedString;
        }
    }

    setValue = (value) => {
        const normalizedValue = NumberInput.normalizeValue(value);

        if (normalizedValue === this.state.value) {
            return;
        }

        // Controlled
        if (this.props.value !== undefined) {
            this.props.onChange(normalizedValue);
            return;
        }

        // Uncontrolled
        this.setState({
            value: normalizedValue,
        }, () => {
            this.props.onChange(this.state.value);
        });
    };

    onFocus = (ev) => {
        this.setState({
            focused: true
        }, () => {
            this.props.onFocus(ev);
        });
    };

    onBlur = (ev) => {
        const {min, max} = this.props;
        const {value} = this.state;

        if (value !== null && (min !== undefined || max !== undefined)) {
            if (min !== undefined && value < min) {
                this.setValue(min);
                ev.target.value = this.inputValueFromNumber(min);
            }

            if (max !== undefined && value > max) {
                this.setValue(max);
                ev.target.value = this.inputValueFromNumber(max);
            }
        } else {
            ev.target.value = this.numberToString(this.stringToNumber(ev.target.value, true));
        }

        this.setState({
            focused: false
        }, () => {
            this.props.onBlur(ev);
        });
    };

    onPaste = (ev) => {
        ev.preventDefault();

        for (let i = 0; i < ev.clipboardData.items.length; i++) {
            const item = ev.clipboardData.items[i];
            if (item.type === 'text/plain') {
                item.getAsString(pastedStr => {
                    const text = this.inputEl.value;
                    const selectionStart = this.inputEl.selectionStart;
                    const selectionEnd = this.inputEl.selectionEnd;

                    const [newNumber, newText, newSelectionStart] = this.handleReplaceSelection(pastedStr, text, selectionStart, selectionEnd);

                    this.inputEl.value = newText;
                    this.inputEl.selectionStart = this.inputEl.selectionEnd = newSelectionStart;
                    this.setValue(newNumber);
                });
                break;
            }
        }
    };

    onCut = (ev) => {
        const text = this.inputEl.value;
        const selectionStart = this.inputEl.selectionStart;
        const selectionEnd = this.inputEl.selectionEnd;

        const [newNumber, newText, newSelectionStart] = this.handleReplaceSelection('', text, selectionStart, selectionEnd);

        this.inputEl.value = newText;
        this.inputEl.selectionStart = this.inputEl.selectionEnd = newSelectionStart;
        this.setValue(newNumber);
    };

    onChange = (ev) => {
        const nev = ev.nativeEvent;
        const insertedText = nev.data;

        let text = ev.target.value;
        let selectionStart = ev.target.selectionStart;

        if (insertedText) {
            // Rollback to the current value
            // if inserted text is not numeric
            if (!this.isMatch(insertedText, /\d/)) {
                text = this.numberToString(numberStringify(this.state.value));
                selectionStart = Math.max(0, selectionStart - insertedText.length);
            }
        }

        const [newNumber, newText, newSelectionStart] = this.handleReplaceSelection('', text, selectionStart, selectionStart);

        this.inputEl.value = newText;
        this.inputEl.selectionStart = this.inputEl.selectionEnd = newSelectionStart;
        this.setValue(newNumber);
    };

    onKeyDown = (ev) => {
        if (ev.ctrlKey || ev.altKey) {
            return;
        }

        if (this.isMatch(ev.key, this.getFunctionalKeyPattern())) {
            return;
        }

        ev.preventDefault();

        if (this.props.step) {
            if (ev.key === 'ArrowUp') {
                this.stepUp();
                return;
            }

            if (ev.key === 'ArrowDown') {
                this.stepDown();
                return;
            }
        }

        const key = ev.key;
        const text = ev.target.value;
        const selectionStart = ev.target.selectionStart;
        const selectionEnd = ev.target.selectionEnd;
        const before = text.substring(0, selectionStart);
        const behind = text.substring(selectionEnd, text.length);

        const args = [key, text, selectionStart, selectionEnd, before, behind];

        const newValues = this.measureNewValuesOnNumberKeys(...args) || this.measureNewValuesOnSpecialKeys(...args);

        if (newValues !== null) {
            const [newNumber, newText, newSelectionStart] = newValues;
            ev.target.value = newText;
            ev.target.selectionStart = ev.target.selectionEnd = newSelectionStart;
            this.setValue(newNumber);
        }
    };

    onKeyUp = (ev) => {

    };

    stepUp = () => {
        const {step, max} = this.props;
        const {value} = this.state;

        const maxDecimalDigits = Math.max(this.countDecimalDigits(step), this.countDecimalDigits(value));
        const K = Math.pow(10, maxDecimalDigits);

        let newNumber = Math.round(value * K + step * K) / K;
        if (max !== undefined) {
            newNumber = Math.min(max, newNumber);
        }

        const newText = this.numberToString(String(newNumber));

        this.inputEl.value = newText;
        this.setValue(newNumber);
    };

    stepDown = () => {
        const {step, min} = this.props;
        const {value} = this.state;

        const maxDecimalDigits = Math.max(this.countDecimalDigits(step), this.countDecimalDigits(value));
        const K = Math.pow(10, maxDecimalDigits);

        let newNumber = Math.round(value * K - step * K) / K;
        if (min !== undefined) {
            newNumber = Math.max(min, newNumber);
        }

        const newText = this.numberToString(String(newNumber));

        this.inputEl.value = newText;
        this.setValue(newNumber);
    };

    continuationTrigger = null;
    continuation = null;

    startContinuation = (act) => {
        act();

        this.continuationTrigger = setTimeout(() => {
            this.continuation = setInterval(act, 30);
        }, 300);
    };

    stopContinuation = () => {
        clearTimeout(this.continuationTrigger);
        clearInterval(this.continuation);
    };

    countDecimalDigits = (num) => {
        const parts = String(num).split('.');

        if (parts.length < 2) {
            return 0;
        }

        return parts[1].length;
    };

    inputValueFromNumber = (value) => {
        return this.numberToString(value === null ? '' : numberStringify(value), true);
    };

    measureNewValuesOnNumberKeys = (key, text, selectionStart, selectionEnd, before, behind) => {
        if (!this.isMatch(key, /\d/)) {
            return null;
        }

        // Don't need to handle aliases
        // because here the text is normalized
        const {negativeSign, decimalPoint, groupSeparator, preventsNegative} = this.props;

        // ensure number string not exceed max digits
        // some cases cannot catch here will be trimmed on key up
        // however it will lead to unwanted "jumping" effect
        const isSelectedARange = selectionStart !== selectionEnd;
        const isBeforeSign = preventsNegative ? false : (
            selectionStart === 0 && text.startsWith(negativeSign)
        );
        if (!isSelectedARange && (isBeforeSign || !this.canAddMoreDigits(text, selectionStart))) {
            return null;
        }

        const caretInsideNegativeSign = this.findCaretPosInTokenBridge(negativeSign, before, behind);
        const caretInsideDecimalPoint = this.findCaretPosInTokenBridge(decimalPoint, before, behind);
        const caretInsideGroupSeparator = this.findCaretPosInTokenBridge(groupSeparator, before, behind);

        // When inserting a digit inside a token, always move the digit to behind of that token.
        // With group separators, we can remove them
        let beforeDelLen = 0;
        let behindDelLen = 0;
        let beforeAdd = '';

        if (caretInsideNegativeSign !== null) {
            beforeDelLen = caretInsideNegativeSign;
            behindDelLen = negativeSign.length - caretInsideNegativeSign;
            beforeAdd = negativeSign;
        }
        else if (caretInsideDecimalPoint !== null) {
            beforeDelLen = caretInsideDecimalPoint;
            behindDelLen = decimalPoint.length - caretInsideDecimalPoint;
            beforeAdd = decimalPoint;
        }
        else if (caretInsideGroupSeparator !== null) {
            beforeDelLen = caretInsideGroupSeparator;
            behindDelLen = groupSeparator.length - caretInsideGroupSeparator;
        }

        const newBefore = before.substring(0, before.length - beforeDelLen) + beforeAdd + key;
        const newBehind = behind.substring(behindDelLen, behind.length);
        const newNumber = this.stringToNumber(newBefore + newBehind);
        const newText = this.numberToString(newNumber);
        const newSelectionStart = selectionStart + this.getSelectionChange(newText, newBefore, before);

        return [newNumber, newText, newSelectionStart];
    };

    measureNewValuesOnSpecialKeys = (key, text, selectionStart, selectionEnd, before, behind) => {
        // Don't need to handle aliases
        // because here the text is normalized
        const {negativeSign, decimalPoint, groupSeparator, preventsNegative, preventsDecimal} = this.props;

        const caretInsideNegativeSign = this.findCaretPosInTokenBridge(negativeSign, before, behind);
        const caretInsideDecimalPoint = this.findCaretPosInTokenBridge(decimalPoint, before, behind);
        const caretInsideGroupSeparator = this.findCaretPosInTokenBridge(groupSeparator, before, behind);

        if (key === 'Backspace' || key === 'Delete') {
            const bak = key === 'Backspace';
            let beforeDelLen = 0;
            let behindDelLen = 0;

            if (selectionStart !== selectionEnd) {
                beforeDelLen = 0;
                behindDelLen = 0;
            }
            // If caret is inside tokens
            else if (caretInsideNegativeSign !== null) {
                beforeDelLen = caretInsideNegativeSign;
                behindDelLen = negativeSign.length - caretInsideNegativeSign;
            }
            else if (caretInsideDecimalPoint !== null) {
                beforeDelLen = caretInsideDecimalPoint;
                behindDelLen = decimalPoint.length - caretInsideDecimalPoint;
            }
            else if (caretInsideGroupSeparator !== null) {
                beforeDelLen = caretInsideGroupSeparator;
                behindDelLen = groupSeparator.length - caretInsideGroupSeparator;
                if (bak) {
                    beforeDelLen++;
                } else {
                    behindDelLen++;
                }
            }
            // If caret is behind of tokens
            else if (bak && before.endsWith(negativeSign)) {
                beforeDelLen = negativeSign.length;
            }
            else if (bak && before.endsWith(decimalPoint)) {
                beforeDelLen = decimalPoint.length;
            }
            else if (bak && before.endsWith(groupSeparator)) {
                beforeDelLen = groupSeparator.length + 1;
            }
            // If caret is before of tokens
            else if (!bak && behind.startsWith(negativeSign)) {
                behindDelLen = negativeSign.length;
            }
            else if (!bak && behind.startsWith(decimalPoint)) {
                behindDelLen = decimalPoint.length;
            }
            else if (!bak && behind.startsWith(groupSeparator)) {
                behindDelLen = groupSeparator.length + 1;
            }
            // Delete digits normally
            else {
                if (bak) {
                    beforeDelLen = 1;
                } else {
                    behindDelLen = 1;
                }
            }

            const newBefore = before.substring(0, before.length - beforeDelLen);
            const newBehind = behind.substring(behindDelLen, behind.length);
            const newNumber = this.stringToNumber(newBefore + newBehind);
            const newText = this.numberToString(newNumber);
            const newSelectionStart = selectionStart + this.getSelectionChange(newText, newBefore, before);

            return [newNumber, newText, newSelectionStart];
        }

        if (!preventsNegative && selectionStart === 0 &&
            this.isMatch(key, '^' + this.getNegativeSignTriggeringPattern() + '$')
        ) {
            const newBehind = behind.replace(new RegExp(this.getNegativeSignParsingPattern(), 'g'), '');
            const newNumber = this.stringToNumber(negativeSign + newBehind);
            const newText = this.numberToString(newNumber);
            const newSelectionStart = newText.includes(negativeSign)
                ? newText.indexOf(negativeSign) + negativeSign.length
                : selectionStart;

            return [newNumber, newText, newSelectionStart];
        }

        if (!preventsDecimal && before !== '' &&
            caretInsideNegativeSign === null && caretInsideDecimalPoint === null &&
            this.isMatch(key, '^' + this.getDecimalSeparatorTriggeringPattern() + '$')
        ) {
            let beforeDelLen = 0;
            let behindDelLen = 0;

            if (caretInsideGroupSeparator !== null) {
                beforeDelLen = caretInsideGroupSeparator;
                behindDelLen = groupSeparator.length - caretInsideGroupSeparator;
            }

            const newBefore = before
                .substring(0, before.length - beforeDelLen)
                .replace(new RegExp(this.getDecimalSeparatorParsingPattern(), 'g'), '');
            const newBehind = behind
                .substring(behindDelLen, behind.length)
                .replace(new RegExp(this.getDecimalSeparatorParsingPattern(), 'g'), '');
            const newNumber = this.stringToNumber(newBefore + decimalPoint + newBehind);
            const newText = this.numberToString(newNumber);
            const newSelectionStart = newText.includes(decimalPoint)
                ? newText.indexOf(decimalPoint) + decimalPoint.length
                : selectionStart;

            return [newNumber, newText, newSelectionStart];
        }

        return null;
    };

    handleReplaceSelection = (replacement, text, selectionStart, selectionEnd) => {
        const before = text.substring(0, selectionStart);
        const behind = text.substring(selectionEnd, text.length);

        const _replacement = isNaN(replacement)
            ? replacement
            : this.numberToString(replacement);

        const _before = !this.isMatch(_replacement, this.getDecimalSeparatorParsingPattern())
            ? before
            : before.replace(new RegExp(this.getDecimalSeparatorParsingPattern(), 'g'), '');

        const newBefore = _before + _replacement;
        const newNumber = this.stringToNumber(newBefore + behind);
        const newText = this.numberToString(newNumber);

        const selectionChange = this.getSelectionChange(newText, newBefore, before);
        const newSelectionStart = selectionStart + selectionChange;

        return [newNumber, newText, newSelectionStart];
    };

    getSelectionChange = (realNewText, tempNewBefore, realBefore) => {
        const {decimalPoint} = this.props;

        let digitCount = 0;
        for (let i = 0; i < tempNewBefore.length; i++) {
            const char = tempNewBefore.charAt(i);
            const subtext = tempNewBefore.substring(i, tempNewBefore.length);
            if (this.isMatch(char, /\d/) || subtext.startsWith(decimalPoint)) {
                digitCount++;
            }
        }

        let comparedDigitCount = 0;
        let realNewBeforeLength = tempNewBefore.length;
        for (let i = 0; i < realNewText.length; i++) {
            const char = realNewText.charAt(i);
            const subtext = realNewText.substring(i, realNewText.length);
            if (this.isMatch(char, /\d/) || subtext.startsWith(decimalPoint)) {
                comparedDigitCount++;
            }

            if (comparedDigitCount === digitCount) {
                realNewBeforeLength = i + 1;
            } else if (comparedDigitCount > digitCount) {
                realNewBeforeLength = i;
                break;
            }
        }

        return realNewBeforeLength - realBefore.length;
    };

    findCaretPosInTokenBridge = (token, beforeCaret, behindCaret) => {
        for (let i = 1; i < token.length; i++) {
            if (beforeCaret.endsWith(token.substring(0, i)) && behindCaret.startsWith(token.substring(i, token.length))) {
                return i;
            }
        }

        return null;
    };

    filterDigitsOnly = (str) => {
        return str
            .replace(new RegExp(this.getNegativeSignParsingPattern(), 'g'), '')
            .replace(new RegExp(this.getDecimalSeparatorParsingPattern(), 'g'), '')
            .replace(new RegExp(this.getGroupsSeparatorParsingPattern(), 'g'), '')
            .replace(/[^\d]/g, '');
    };

    canAddMoreDigits = (normText, caretPos) => {
        const {maxDigits, maxIntegerDigits, maxFractionalDigits} = this.getMaxDigitsConstraints();

        // Don't need to capture aliases
        // because here the text is normalized
        // It even be better to capture exact token
        const {decimalPoint} = this.props;
        const [intPart, fraPart] = normText.split(decimalPoint, 2);

        const intCount = this.filterDigitsOnly(intPart).length;
        const fraCount = fraPart ? this.filterDigitsOnly(fraPart).length : 0;
        if (intCount + fraCount >= maxDigits) {
            return false;
        }

        const isAddFraDigit = caretPos > intPart.length;
        if (isAddFraDigit) {
            return fraCount < maxFractionalDigits;
        }

        return intCount < maxIntegerDigits;
    };

    isMatch = (str, patternOrRegex) => {
        return str.match(patternOrRegex instanceof RegExp ? patternOrRegex : new RegExp(patternOrRegex)) !== null;
    };

    trimNumberString = (numStr) => {
        const {maxDigits, maxIntegerDigits, maxFractionalDigits} = this.getMaxDigitsConstraints();

        const decPointMatch = numStr.match(new RegExp(this.getDecimalSeparatorParsingPattern()));

        let intStr, fraStr;
        if (decPointMatch === null) {
            intStr = numStr;
            fraStr = '';
        } else {
            const [intSegment, ...fraSegments] = numStr.split(new RegExp(this.getDecimalSeparatorParsingPattern()));
            intStr = intSegment;
            fraStr = fraSegments.join('');
        }

        let intOut = '';
        let intCount = 0;
        for (let i = 0; i < intStr.length && intCount < maxIntegerDigits && intCount < maxDigits; i++) {
            const ch = intStr.charAt(i);
            intOut += ch;

            if (ch.match(/\d/) !== null) {
                intCount++;
            }
        }

        if (decPointMatch === null) {
            return intOut;
        }

        let fraOut = '';
        let fraCount = 0;
        for (let i = 0; i < fraStr.length && fraCount < maxFractionalDigits && intCount + fraCount < maxDigits; i++) {
            const ch = fraStr.charAt(i);
            fraOut += ch;

            if (ch.match(/\d/) !== null) {
                fraCount++;
            }
        }

        return [intOut, decPointMatch[0], fraOut].filter(t => t !== '').join('');
    };

    /**
     *
     * @param {string} str
     * @param {boolean} strict
     * @param {boolean} ignoresConstraints
     * @returns {string}
     */
    stringToNumber = (str, strict = false, ignoresConstraints = false) => {
        const {preventsNegative, preventsDecimal} = this.props;

        if (!ignoresConstraints) {
            str = this.trimNumberString(str);
        }

        let signPart = '';

        const signPattern = '^' + this.getNegativeSignParsingPattern();

        if (this.isMatch(str, signPattern)) {
            str = str.replace(new RegExp(signPattern), '');

            if (!preventsNegative || ignoresConstraints) {
                signPart = '-';
            }
        }

        const hasDecimal = (!preventsDecimal || ignoresConstraints) &&
            this.isMatch(str, this.getDecimalSeparatorParsingPattern());

        if (!hasDecimal) {
            let digitsOnlyStr = this.filterDigitsOnly(str);

            if (!strict) {
                return signPart + digitsOnlyStr;
            }

            const numberPart = digitsOnlyStr === '' ? '' : String(Number(digitsOnlyStr));

            return (numberPart === '' || numberPart === '0' ? '' : signPart) + numberPart;
        }

        let [intPart, ...decParts] = str
            .split(new RegExp(this.getDecimalSeparatorParsingPattern()))
            .map(t => this.filterDigitsOnly(t));

        if (!strict) {
            return signPart + (intPart === '' ? '' : (intPart + '.')) + decParts.join('');
        }

        intPart = intPart.replace(/^0+/, '0');
        decParts = decParts.filter(t => t !== '');

        const decs = decParts.join('').replace(/0+$/, '');
        const numberPart = (intPart === '' ? '0' : Number(intPart)) + (decs.length > 0 ? '.' + decs : '');

        return (numberPart === '' || numberPart === '0' ? '' : signPart) + numberPart;
    };

    /**
     *
     * @param {string} num
     * @param {boolean} ignoresConstraints
     * @returns {string}
     */
    numberToString = (num, ignoresConstraints = false) => {
        const {negativeSign, decimalPoint, preventsNegative, preventsDecimal} = this.props;

        let sign = '';
        let posNum = num;

        if (num.startsWith('-')) {
            if (!preventsNegative || ignoresConstraints) {
                sign = negativeSign;
            }
            posNum = num.substring(1, num.length);
        }

        const parts = posNum.split('.');

        if (parts.length === 1) {
            return sign + this.groupIntegerPart(parts[0]);
        }

        const decPoint = (!preventsDecimal || ignoresConstraints) ? decimalPoint : '';

        return sign
            + this.groupIntegerPart(parts[0])
            + decPoint
            + this.groupFractionalPart(parts[1]);
    };

    groupIntegerPart = (numStr) => {
        const {groupSeparator, groupSize, groupsIntegerDigits} = this.props;

        if (!groupsIntegerDigits) {
            return numStr;
        }

        let str = '';

        let k = 0;

        for (let i = numStr.length - 1; i >= 0; i--) {
            if (k > 0 && k % groupSize === 0) {
                str = groupSeparator + str;
            }

            k++;

            str = numStr.charAt(i) + str;
        }

        return str;
    };

    groupFractionalPart = (numStr) => {
        const {groupSeparator, groupSize, groupsFractionalDigits} = this.props;

        if (!groupsFractionalDigits) {
            return numStr;
        }

        let str = '';

        for (let i = 0; i < numStr.length; i++) {
            if (i > 0 && i % groupSize === 0) {
                str += groupSeparator;
            }

            str += numStr.charAt(i);
        }

        return str;
    };

    getMaxDigitsConstraints = () => {
        const {
            maxDigits,
            maxIntegerDigits = maxDigits,
            maxFractionalDigits = maxDigits,
        } = this.props;

        return {
            maxDigits,
            maxIntegerDigits,
            maxFractionalDigits,
        };
    };

    generateParsingPattern = (token, aliases) => {
        // Token patterns are used to parse random strings that are not normalized
        // Do not use this when handling input text value
        // Instead, use the token directly

        if (aliases.length > 0) {
            const inner = Array
                .from(new Set([token, ...aliases])) // ensure unique
                .map(t => regexEscapeString(t)) // regex escape
                .join('|');

            return '(?:' + inner + ')';
        }

        return regexEscapeString(token);
    };

    generateTriggeringPattern = (token, triggers) => {
        // Trigger patterns are used to handle key event
        // Then insert the corresponding token
        
        if (triggers.length > 0) {
            const inner = Array
                .from(new Set(triggers)) // ensure unique
                .map(t => regexEscapeString(t)) // regex escape
                .join('|');

            return '(?:' + inner + ')';
        }

        return regexEscapeString(token);
    };

    getDecimalSeparatorParsingPattern = () => {
        return this.generateParsingPattern(
            this.props.decimalPoint,
            this.props.decimalPointAliases
        );
    };

    getDecimalSeparatorTriggeringPattern = () => {
        return this.generateTriggeringPattern(
            this.props.decimalPoint,
            this.props.decimalPointTriggers
        );
    };

    getNegativeSignParsingPattern = () => {
        return this.generateParsingPattern(
            this.props.negativeSign,
            this.props.negativeSignAliases
        );
    };

    getNegativeSignTriggeringPattern = () => {
        return this.generateTriggeringPattern(
            this.props.negativeSign,
            this.props.negativeSignTriggers
        );
    };

    getGroupsSeparatorParsingPattern = () => {
        return this.generateParsingPattern(
            this.props.groupSeparator,
            this.props.groupSeparatorAliases
        );
    };

    getFunctionalKeyPattern = () => {
        return '^Tab|Shift|Control|Alt|Enter|ArrowLeft|ArrowRight|Home|End|(?:F[0-9]+)$';
    };

    render() {
        const {disabled, unit, step, appearance, align, prompt} = this.props;
        const {value, focused} = this.state;

        return <div
            className={
                `number-input appearance-${appearance}`
                + (focused ? ' focused' : '')
                + (disabled ? ' disabled' : '')
            }
            onClick={() => this.inputEl.focus()}
        >
            <input
                type="text"
                onKeyUp={this.onKeyUp}
                onKeyDown={this.onKeyDown}
                onFocus={this.onFocus}
                onBlur={this.onBlur}
                onPaste={this.onPaste}
                onCut={this.onCut}
                onChange={this.onChange}
                defaultValue={this.inputValueFromNumber(value)}
                placeholder={prompt}
                disabled={disabled}
                spellCheck={false}
                style={{textAlign: align}}
                ref={el => this.inputEl = el}
            />

            {
                unit &&
                <span className="unit">{unit}</span>
            }

            {
                step &&
                !disabled &&
                <div className="controls">
                    <button
                        type="button"
                        className="step-up-button"
                        onMouseDown={(ev) => {
                            ev.preventDefault(); // prevent blur
                            this.startContinuation(this.stepUp);
                        }}
                        onMouseUp={() => this.stopContinuation()}
                        onMouseLeave={() => this.stopContinuation()}
                        tabIndex="-1"
                    >
                        <Icon name="angle-up"/>
                    </button>
                    <button
                        type="button"
                        className="step-down-button"
                        onMouseDown={(ev) => {
                            ev.preventDefault(); // prevent blur
                            this.startContinuation(this.stepDown);
                        }}
                        onMouseUp={() => this.stopContinuation()}
                        onMouseLeave={() => this.stopContinuation()}
                        tabIndex="-1"
                    >
                        <Icon name="angle-down"/>
                    </button>
                </div>
            }
        </div>;
    }
}


const isCommaStyle = getLocale() === 'vi';

NumberInput.defaultProps = {
    defaultValue: null,
    imperativeValue: undefined,
    value: undefined,
    onChange: (value) => undefined,
    disabled: false,
    unit: undefined,
    step: undefined,
    appearance: 'box', // box, lined
    align: 'left',
    prompt: '',
    maxDigits: 15,
    maxIntegerDigits: undefined,
    maxFractionalDigits: undefined,
    preventsNegative: false,
    preventsDecimal: false,
    groupsIntegerDigits: true,
    groupsFractionalDigits: false,
    negativeSign: '-',
    negativeSignAliases: [],
    negativeSignTriggers: [],
    decimalPoint: isCommaStyle ? ',' : '.',
    decimalPointAliases: [],
    decimalPointTriggers: [],
    groupSize: 3,
    groupSeparator: isCommaStyle ? '.' : ',',
    groupSeparatorAliases: [],
    onFocus: (ev) => undefined,
    onBlur: (ev) => undefined,
};


/******************************************************************
 * Converts e-Notation Numbers to Plain Numbers
 ******************************************************************
 * @function eToNumber(number)
 * @version  1.00
 * @param   {Number} num valid Number in exponent format.
 *          pass number as a string for very large 'e' numbers or with large fractions
 *          (none 'e' number returned as is).
 * @return  {string}  a decimal number string.
 * @author  Mohsen Alyafei
 * @date    17 Jan 2020
 * Note: No check is made for NaN or undefined input numbers.
 *
 *****************************************************************/
function numberStringify(num) {
    let sign = "";
    (num += "").charAt(0) == "-" && (num = num.substring(1), sign = "-");
    let arr = num.split(/[e]/ig);
    if (arr.length < 2) return sign + num;
    let dot = '.', n = arr[0], exp = +arr[1],
        w = (n = n.replace(/^0+/, '')).replace(dot, ''),
        pos = n.split(dot)[1] ? n.indexOf(dot) + exp : w.length + exp,
        L   = pos - w.length, s = "" + BigInt(w);
    w   = exp >= 0 ? (L >= 0 ? s + "0".repeat(L) : r()) : (pos <= 0 ? "0" + dot + "0".repeat(Math.abs(pos)) + s : r());
    L= w.split(dot); if (L[0]==0 && L[1]==0 || (+w==0 && +s==0) ) w = 0; //** added 9/10/2021
    return sign + w;
    function r() {return w.replace(new RegExp(`^(.{${pos}})(.)`), `$1${dot}$2`)}
}
